import React, { useState, useEffect } from 'react';
import {
  Gamepad2,
  Sparkles,
  Smartphone,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Puzzle,
  Trophy,
  Zap,
  ShieldCheck,
  Users,
  Clock,
} from 'lucide-react';

const copy = {
  nav: {
    games: 'Juegos',
    howItWorks: 'Cómo funciona',
    categories: 'Categorías',
    faq: 'Preguntas frecuentes',
  },
  hero: {
    badge: 'Diversión en tu móvil',
    title: 'Juegos casuales para jugar cuando quieras',
    subtitle:
      'Pixipals es tu espacio de minijuegos y retos pensados para el móvil: rompecabezas, reflejos, trivia y más. Entra desde el teléfono, juega en sesiones cortas y descubre novedades con la comunidad.',
    cta: 'Quiero empezar',
    trust: 'Pensado para pantallas táctiles y uso diario',
  },
  games: {
    title: 'Tipos de juegos',
    subtitle: 'Variedad para todos los gustos — sesiones rápidas, siempre a mano',
    items: [
      { name: 'Rompecabezas y lógica', tag: 'Concentración' },
      { name: 'Arcade y reflejos', tag: 'Ritmo rápido' },
      { name: 'Trivia y conocimiento', tag: 'Retos mentales' },
      { name: 'Carreras y habilidad', tag: 'Precisión' },
      { name: 'Memoria y atención', tag: 'Entrena la mente' },
      { name: 'Casual y relajación', tag: 'Sin complicaciones' },
    ],
    cta: 'Acceder',
  },
  howItWorks: {
    title: 'Cómo funciona',
    subtitle: 'Tres pasos para disfrutar los juegos en tu móvil',
    steps: [
      {
        title: 'Te suscribes',
        description:
          'Formas parte de la comunidad Pixipals mediante el código 1281, según las condiciones del servicio para usuarios ICE.',
      },
      {
        title: 'Recibes el acceso',
        description:
          'Obtienes la información y el enlace para jugar desde un móvil compatible con internet.',
      },
      {
        title: 'Juegas cuando quieras',
        description:
          'Abres los minijuegos en el navegador o la experiencia indicada y disfrutas partidas cortas en cualquier momento.',
      },
    ],
  },
  benefits: {
    title: 'Por qué Pixipals',
    subtitle: 'Una experiencia ligera, social y hecha para el día a día',
    items: [
      {
        title: 'Actualizado con frecuencia',
        description: 'Nuevos retos y variaciones para que siempre tengas algo distinto que probar.',
      },
      {
        title: 'Pensado para móvil',
        description: 'Controles táctiles, cargas rápidas y diseño adaptable a pantallas pequeñas.',
      },
      {
        title: 'Comunidad por suscripción',
        description: 'Formas parte de Pixipals con las reglas claras del servicio y el código 1281.',
      },
      {
        title: 'Atención cuando la necesitas',
        description: 'Soporte al cliente disponible en el 22710290 para dudas sobre el servicio.',
      },
    ],
  },
  popular: {
    title: 'Juega en cualquier momento',
    subtitle: 'Ideal para el transporte, el descanso o un ratito libre',
    bannerTitle: 'Minijuegos en tu bolsillo',
    bannerText:
      'Entra desde tu smartphone, elige un juego y vuelve cuando quieras. Sin instalaciones complicadas: todo pensado para que empieces al instante.',
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        q: '¿En qué dispositivos funciona?',
        a: 'Pixipals está pensado para móviles con acceso a internet. El usuario es responsable de comprobar la compatibilidad de su terminal con el servicio.',
      },
      {
        q: '¿Cómo me doy de baja?',
        a: 'Para solicitar la desafiliación debes enviar la palabra BAJA al código 1281, según las condiciones indicadas en el servicio.',
      },
      {
        q: '¿Qué incluye la suscripción?',
        a: 'Formas parte de la comunidad Pixipals por suscripción al código 1281. Recibirás un mensaje diario con el valor indicado en las condiciones del servicio; el cobro será automático a tu facturación celular o descontado del saldo principal, según corresponda.',
      },
      {
        q: '¿Hay devoluciones?',
        a: 'No aplican devoluciones ni reembolsos, salvo lo que disponga la normativa aplicable o las condiciones legales que nos facilites.',
      },
      {
        q: '¿A quién contacto si tengo un problema?',
        a: 'Para atención al cliente puedes comunicarte al 22710290.',
      },
    ],
  },
  cta: {
    title: '¿Listo para jugar?',
    subtitle: 'Únete a la comunidad Pixipals y lleva los minijuegos contigo en el móvil.',
    button: 'Solicitar acceso',
  },
  footer: {
    privacy: 'Política de privacidad',
    terms: 'Términos y condiciones',
    contact: 'Contacto',
    legalNote:
      'Los documentos legales definitivos serán publicados en esta web cuando estén disponibles.',
  },
  disclaimer:
    'Este servicio es único y exclusivo para usuarios ICE. Pixipals es una comunidad por suscripción del código 1281. El usuario recibirá un mensaje diario con un valor de 250 colones IVI y el cobró será automático a su facturación celular o bien descargado del saldo principal de su cuenta. Para solicitar la desafiliación del servicio deberá de enviar la palabra BAJA al código 1281. La suscripción será indefinida y se mantendrá vigente mientras el usuario no la cancele. El usuario será responsable de verificar compatibilidad con su terminal. No aplican devoluciones ni reembolsos. Para atención al cliente dirigirse al 22710290.',
};

const gameIcons = [Puzzle, Zap, Trophy, Gamepad2, Sparkles, Users];
const gameColors = [
  'from-violet-500 to-purple-600',
  'from-cyan-500 to-blue-600',
  'from-amber-500 to-orange-600',
  'from-emerald-500 to-teal-600',
  'from-pink-500 to-rose-600',
  'from-indigo-500 to-violet-600',
];

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const t = copy;

  return (
    <div className="min-h-screen bg-white font-body text-dark">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-dark">
                Pixi<span className="text-primary">pals</span>
              </span>
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="#juegos" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">
                {t.nav.games}
              </a>
              <a href="#como-funciona" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">
                {t.nav.howItWorks}
              </a>
              <a href="#categorias" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">
                {t.nav.categories}
              </a>
              <a href="#faq" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">
                {t.nav.faq}
              </a>
            </nav>

            <div className="flex items-center space-x-3">
              <a
                href="/s"
                className="inline-flex items-center bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary-dark transition-colors font-semibold text-sm shadow-sm"
              >
                {t.hero.cta}
              </a>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-dark hover:bg-gray-100 transition-colors"
                aria-expanded={mobileMenuOpen}
                aria-label="Menú"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {[
                { href: '#juegos', label: t.nav.games },
                { href: '#como-funciona', label: t.nav.howItWorks },
                { href: '#categorias', label: t.nav.categories },
                { href: '#faq', label: t.nav.faq },
              ].map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-dark hover:text-primary transition-colors font-medium rounded-lg hover:bg-light"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/s"
                className="block text-center bg-primary text-white px-4 py-3 rounded-lg font-semibold mt-2"
              >
                {t.hero.cta}
              </a>
            </div>
          </div>
        )}
      </header>

      <section className="pt-24 pb-20 md:pt-32 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-cyan-50/40 to-white" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Sparkles className="w-4 h-4" />
                <span>{t.hero.badge}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-6 font-heading leading-tight">
                {t.hero.title}
              </h1>
              <p className="text-lg text-muted mb-8 leading-relaxed max-w-xl">{t.hero.subtitle}</p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href="/s"
                  className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30"
                >
                  {t.hero.cta}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
                <div className="flex items-center space-x-2 text-sm text-muted">
                  <Smartphone className="w-5 h-5 text-primary" />
                  <span>{t.hero.trust}</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/pixipals-hero-juegos.jpg"
                alt="Persona jugando minijuegos en el móvil, estilo colorido y casual"
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[420px] lg:max-h-none"
              />
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-xl shadow-lg border border-gray-100 hidden sm:block">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                    <Gamepad2 className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-bold text-dark">Pixipals</p>
                    <p className="text-xs text-muted">Minijuegos</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-lg border border-gray-100 hidden sm:block">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                    <Puzzle className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-bold text-dark">+6</p>
                    <p className="text-xs text-muted">Estilos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="juegos" className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.games.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.games.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.games.items.map((item, i) => {
              const Icon = gameIcons[i];
              return (
                <a
                  key={item.name}
                  href="/s"
                  className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 overflow-hidden"
                >
                  <div
                    className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${gameColors[i]} opacity-10 rounded-bl-full group-hover:opacity-20 transition-opacity`}
                  />
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gameColors[i]} flex items-center justify-center mb-4`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-dark text-lg mb-1">{item.name}</h3>
                  <p className="text-primary font-semibold text-sm mb-4">{item.tag}</p>
                  <span className="inline-flex items-center text-sm font-medium text-primary group-hover:translate-x-1 transition-transform">
                    {t.games.cta} <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section id="como-funciona" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.howItWorks.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.howItWorks.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20" />
            {t.howItWorks.steps.map((step, i) => {
              const icons = [Users, Smartphone, Gamepad2];
              const Icon = icons[i];
              return (
                <div key={step.title} className="text-center relative">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary text-white rounded-2xl mb-6 text-xl font-bold shadow-lg shadow-primary/20 relative z-10">
                    {i + 1}
                  </div>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 text-primary rounded-xl mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark mb-3 font-heading">{step.title}</h3>
                  <p className="text-muted leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <a
              href="/s"
              className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-all shadow-lg shadow-primary/25"
            >
              {t.hero.cta}
              <ChevronRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="/images/pixipals-movil-jugando.jpg"
                alt="Smartphone mostrando pantalla de minijuegos Pixipals"
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[520px]"
              />
              <div className="absolute -bottom-4 -right-4 bg-secondary text-white p-4 rounded-xl shadow-lg">
                <p className="font-bold text-2xl">24/7</p>
                <p className="text-sm text-white/80">Cuando quieras jugar</p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 font-heading">{t.benefits.title}</h2>
              <p className="text-lg text-muted mb-8">{t.benefits.subtitle}</p>
              <div className="space-y-6">
                {t.benefits.items.map((item, i) => {
                  const icons = [Clock, Smartphone, ShieldCheck, Users];
                  const Icon = icons[i];
                  return (
                    <div key={item.title} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-dark mb-1 font-heading">{item.title}</h3>
                        <p className="text-muted text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8">
                <a
                  href="/s"
                  className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-all shadow-sm"
                >
                  {t.cta.button}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="categorias" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.popular.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.popular.subtitle}</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="/images/pixipals-banner-juegos.jpg"
              alt="Mosaico de minijuegos coloridos en móviles"
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/85 via-dark/55 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 md:px-12 max-w-lg">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-heading">{t.popular.bannerTitle}</h3>
                <p className="text-white/80 mb-6">{t.popular.bannerText}</p>
                <a
                  href="/s"
                  className="inline-flex items-center bg-white text-dark px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                >
                  {t.hero.cta}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="py-20 bg-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.faq.title}</h2>
          </div>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <div key={item.q} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="font-semibold text-dark pr-4">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-muted flex-shrink-0 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-muted leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Gamepad2 className="w-12 h-12 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">{t.cta.title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">{t.cta.subtitle}</p>
          <a
            href="/s"
            className="inline-flex items-center bg-white text-primary px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl"
          >
            {t.cta.button}
            <ChevronRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>

      <footer className="bg-dark text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-2 mb-6">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg">
                Pixi<span className="text-primary">pals</span>
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-6">
              <a href="/privacy.html" className="text-gray-400 hover:text-white transition-colors text-sm">
                {t.footer.privacy}
              </a>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <a href="/terms.html" className="text-gray-400 hover:text-white transition-colors text-sm">
                {t.footer.terms}
              </a>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <a href="/contact.html" className="text-gray-400 hover:text-white transition-colors text-sm">
                {t.footer.contact}
              </a>
            </div>
            <p className="text-gray-500 text-sm mb-4">© {new Date().getFullYear()} Pixipals. Todos los derechos reservados.</p>
            <p className="text-gray-600 text-xs max-w-2xl mx-auto mb-8">{t.footer.legalNote}</p>

            <div className="border-t border-gray-800 pt-8 mt-8">
              <div className="max-w-4xl mx-auto text-left">
                <p className="text-xs text-gray-500 leading-relaxed">{t.disclaimer}</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
